import React from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator } from 'react-native';
import type { SuggestionItem, SubActivityItem } from '../services/llm';
import { useTheme } from '../theme/ThemeContext';
import { useThemedStyles } from '../theme/useThemedStyles';
import type { Theme } from '../theme/types';

interface AiSuggestionsCardProps {
  loading: boolean;
  modelDownloading?: boolean;
  suggestions?: SuggestionItem[];
  subtasks?: SubActivityItem[];
  error?: string | null;
  onApplySuggestion?: (text: string) => void;
  onAddSubtask?: (subtask: SubActivityItem) => void;
  onDismiss: () => void;
}

export default function AiSuggestionsCard({
  loading,
  modelDownloading,
  suggestions,
  subtasks,
  error,
  onApplySuggestion,
  onAddSubtask,
  onDismiss,
}: AiSuggestionsCardProps) {
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();

  if (loading) {
    const loadingText = modelDownloading
      ? 'Downloading AI model — this may take a few minutes...'
      : 'Thinking on-device...';
    return (
      <View style={styles.container} testID="ai-suggestions.loading">
        <View style={styles.loadingRow}>
          <ActivityIndicator size="small" color={colors.ai} accessibilityLabel={loadingText} />
          <Text style={styles.loadingText}>{loadingText}</Text>
        </View>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container} accessibilityRole="alert">
        <Text style={styles.errorText}>{error}</Text>
        <TouchableOpacity
          onPress={onDismiss}
          accessibilityRole="button"
          accessibilityLabel="Dismiss error"
          testID="ai-suggestions.dismiss-error"
        >
          <Text style={styles.dismiss} accessible={false}>Dismiss</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const hasSuggestions = suggestions && suggestions.length > 0;
  const hasSubtasks = subtasks && subtasks.length > 0;

  if (!hasSuggestions && !hasSubtasks) return null;

  return (
    <View style={styles.container} testID="ai-suggestions-card">
      <Text style={styles.title}><Text accessible={false}>{'\u2728'} </Text>AI Suggestions</Text>

      {hasSuggestions &&
        suggestions.map((s, i) => (
          <View key={i} style={styles.suggestionItem}>
            <View style={styles.suggestionContent}>
              <Text style={styles.suggestionText}>{s.text}</Text>
              {s.reasoning ? (
                <Text style={styles.reasoningText}>
                  <Text style={styles.reasoningLabel}>Why: </Text>
                  {s.reasoning}
                </Text>
              ) : null}
            </View>
            {onApplySuggestion && (
              <TouchableOpacity
                style={styles.applyBtn}
                onPress={() => onApplySuggestion(s.text)}
                accessibilityRole="button"
                accessibilityLabel={`Apply suggestion: ${s.text}`}
                accessibilityHint="Applies suggestion text only, not the rationale"
                testID={`ai-suggestions.apply-${i}`}
              >
                <Text style={styles.applyBtnText} accessible={false}>Apply</Text>
              </TouchableOpacity>
            )}
          </View>
        ))}

      {hasSubtasks &&
        subtasks.map((st, i) => (
          <View key={i} style={styles.suggestionItem}>
            <View style={styles.subtaskContent}>
              <Text style={styles.suggestionText}>{st.text}</Text>
              <Text style={styles.reasoningText}>{st.estimated_minutes}m</Text>
            </View>
            {onAddSubtask && (
              <TouchableOpacity
                style={styles.applyBtn}
                onPress={() => onAddSubtask(st)}
                accessibilityRole="button"
                accessibilityLabel={`Add sub-activity: ${st.text}`}
                testID={`ai-suggestions.add-subtask-${i}`}
              >
                <Text style={styles.applyBtnText} accessible={false}>Add</Text>
              </TouchableOpacity>
            )}
          </View>
        ))}

      <TouchableOpacity
        onPress={onDismiss}
        accessibilityRole="button"
        accessibilityLabel="Dismiss suggestions"
        testID="ai-suggestions.dismiss"
      >
        <Text style={styles.dismiss} accessible={false}>Dismiss</Text>
      </TouchableOpacity>
    </View>
  );
}

const createStyles = (theme: Theme) => ({
  container: {
    backgroundColor: theme.colors.aiCardBg,
    borderWidth: 1,
    borderColor: theme.colors.aiCardBorder,
    borderRadius: 10,
    padding: 14,
    marginTop: 10,
  },
  loadingRow: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    gap: 10,
    paddingVertical: 8,
  },
  loadingText: {
    fontSize: 14,
    color: theme.colors.ai,
    fontWeight: '500' as const,
  },
  title: {
    fontSize: 14,
    fontWeight: '700' as const,
    color: theme.colors.ai,
    marginBottom: 10,
  },
  errorText: {
    fontSize: 13,
    color: theme.colors.error,
    lineHeight: 19,
    marginBottom: 8,
  },
  suggestionItem: {
    flexDirection: 'row' as const,
    alignItems: 'flex-start' as const,
    backgroundColor: theme.colors.surface,
    borderRadius: 8,
    padding: 10,
    marginBottom: 6,
    gap: 10,
  },
  suggestionContent: {
    flex: 1,
  },
  suggestionText: {
    fontSize: 14,
    color: theme.colors.aiText,
    lineHeight: 20,
    marginBottom: 4,
  },
  reasoningText: {
    fontSize: 12,
    color: theme.colors.textDisabled,
    marginTop: 6,
    paddingTop: 6,
    borderTopWidth: 1,
    borderTopColor: theme.colors.borderLight,
    fontStyle: 'italic' as const,
  },
  reasoningLabel: {
    fontStyle: 'normal' as const,
    fontWeight: '600' as const,
  },
  subtaskContent: {
    flex: 1,
  },
  applyBtn: {
    backgroundColor: theme.colors.ai,
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    alignSelf: 'center' as const,
  },
  applyBtnText: {
    fontSize: 12,
    fontWeight: '700' as const,
    color: theme.colors.onPrimary,
  },
  dismiss: {
    fontSize: 13,
    color: theme.colors.textDisabled,
    fontWeight: '500' as const,
    marginTop: 8,
    textAlign: 'center' as const,
  },
});
